import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, of, pipe, switchMap, tap} from 'rxjs';
import {AdminOrderService} from '../services/admin-order.service';
import {extractErrorMessage} from '@core/utils/error-handler.util';

export interface Order {
  id: number;
  orderNumber: string;
  customerId?: number;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  restaurantId: number;
  restaurantName: string;
  status: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee?: number;
  discount?: number;
  totalAmount: number;
  deliveryAddress?: string;
  deliveryInstructions?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  statusHistory?: OrderStatusHistory[];
}

export interface OrderItem {
  id: number;
  menuItemId: number;
  menuItemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specialInstructions?: string;
}

export interface OrderStatusHistory {
  status: string;
  timestamp: Date;
  changedBy?: string;
}

export interface OrderFilters {
  status?: string;
  restaurantId?: number;
  date?: string;
  page?: number;
  size?: number;
}

interface AdminOrderState {
  orders: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalElements: number;
  filters: OrderFilters;
}

const initialState: AdminOrderState = {
  orders: [],
  selectedOrder: null,
  loading: false,
  error: null,
  currentPage: 0,
  totalPages: 0,
  totalElements: 0,
  filters: {}
};

export const AdminOrderStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),

  withComputed((state) => ({
    pendingOrders: computed(() =>
      state.orders().filter(o => o.status === 'PENDING')
    ),

    activeOrders: computed(() =>
      state.orders().filter(o =>
        ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'DELIVERING'].includes(o.status)
      )
    ),

    todayOrders: computed(() => {
      const today = new Date().toDateString();
      return state.orders().filter(o =>
        new Date(o.createdAt).toDateString() === today
      );
    }),

    totalRevenue: computed(() =>
      state.orders()
        .filter(o => o.status === 'DELIVERED')
        .reduce((sum, o) => sum + o.totalAmount, 0)
    ),

    hasMore: computed(() =>
      state.currentPage() < state.totalPages() - 1
    )
  })),

  withMethods((store, orderService = inject(AdminOrderService)) => ({
    loadOrders: rxMethod<OrderFilters | void>(
      pipe(
        tap(() => patchState(store, {loading: true, error: null})),
        switchMap((filters) => {
          const currentFilters = filters || store.filters();
          return orderService.getOrders(currentFilters).pipe(
            tap((response: any) => {
              patchState(store, {
                orders: response.content || response,
                loading: false,
                currentPage: response.number || 0,
                totalPages: response.totalPages || 1,
                totalElements: response.totalElements || response.length,
                filters: currentFilters as OrderFilters
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error, 'Erreur lors du chargement des commandes')
              });
              return of(null);
            })
          );
        })
      )
    ),

    loadOrderDetails: rxMethod<number>(
      pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap((orderId) =>
          orderService.getOrderById(orderId).pipe(
            tap((order) => {
              patchState(store, {
                selectedOrder: order,
                loading: false
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error, 'Erreur lors du chargement des détails')
              });
              return of(null);
            })
          )
        )
      )
    ),

    updateOrderStatus: rxMethod<{ orderId: number; status: string }>(
      pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap(({orderId, status}) =>
          orderService.updateOrderStatus(orderId, status).pipe(
            tap((updatedOrder) => {
              const orders = store.orders().map(o =>
                o.id === orderId ? {...o, status, updatedAt: new Date()} : o
              );
              patchState(store, {orders, loading: false});
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error, 'Erreur lors de la mise à jour du statut')
              });
              return of(null);
            })
          )
        )
      )
    ),

    cancelOrder: rxMethod<{ orderId: number; reason: string }>(
      pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap(({orderId, reason}) =>
          orderService.cancelOrder(orderId, reason).pipe(
            tap(() => {
              const orders = store.orders().map(o =>
                o.id === orderId ? {...o, status: 'CANCELLED', updatedAt: new Date()} : o
              );
              patchState(store, {orders, loading: false});
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error, 'Erreur lors de l\'annulation')
              });
              return of(null);
            })
          )
        )
      )
    ),

    // Méthodes synchrones simples
    setFilters(filters: OrderFilters): void {
      patchState(store, {filters});
    },

    clearError(): void {
      patchState(store, {error: null});
    },

    clearSelectedOrder(): void {
      patchState(store, {selectedOrder: null});
    }
  }))
);

